import { useMemo, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { T } from "../lib/theme";
import { elapsed, fmtDate, iso, parseDate, today } from "../lib/dates";
import { typeOf, type Assignment, type Meeting, type MemberSummary } from "../lib/types";
import { EmptyState, Header } from "../components/ui";

type Row = { kind: "meeting"; m: Meeting } | { kind: "gap"; date: string };

export function Meetings({
  meetings,
  members,
  onFillGap,
  onEdit,
}: {
  meetings: Meeting[];
  members: MemberSummary[];
  onFillGap: (date: string) => void;
  onEdit: (m: Meeting) => void;
}) {
  const currentYear = String(today().getFullYear());

  const years = useMemo(() => {
    const set = new Set(meetings.map((m) => m.meeting_date.slice(0, 4)));
    set.add(currentYear);
    return [...set].sort().reverse();
  }, [meetings, currentYear]);

  const [year, setYear] = useState(currentYear);

  const rows = useMemo<Row[]>(() => {
    const y = Number(year);
    const byDate = new Map(meetings.map((m) => [m.meeting_date, m]));
    const inYear = meetings.filter((m) => m.meeting_date.startsWith(year));

    // Walk Sundays backwards from today (current year) or 31 Dec (past years)
    // down to the earliest Sunday recorded in that year. Anything without a
    // meeting row is a week nobody has accounted for yet.
    const end = y === today().getFullYear() ? today() : new Date(y, 11, 31, 12);
    end.setDate(end.getDate() - end.getDay());
    const earliest = inYear.length ? parseDate(inYear[inYear.length - 1].meeting_date) : new Date(y, 0, 1, 12);

    const out: Row[] = [];
    const cursor = new Date(end);
    while (cursor >= earliest && cursor.getFullYear() === y) {
      const d = iso(cursor);
      const m = byDate.get(d);
      out.push(m ? { kind: "meeting", m } : { kind: "gap", date: d });
      cursor.setDate(cursor.getDate() - 7);
    }
    return out;
  }, [meetings, year]);

  const gaps = rows.filter((r) => r.kind === "gap").length;

  const nameOf = (a: Assignment): string => {
    if (a.guest_name) return a.guest_name;
    const m = members.find((x) => x.id === a.member_id);
    return m ? `${m.last_name}, ${m.first_name}` : "—";
  };

  return (
    <>
      <Header
        title="Meetings"
        sub={gaps ? `${gaps} Sunday${gaps > 1 ? "s" : ""} not yet recorded in ${year}` : `All Sundays recorded in ${year}`}
      />

      <div className="yearscroll" style={{ background: T.navy, padding: "0 12px 14px", display: "flex", gap: 6 }}>
        {years.map((y) => (
          <button
            key={y}
            onClick={() => setYear(y)}
            style={{
              padding: "7px 15px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
              background: year === y ? "#fff" : "rgba(255,255,255,.12)",
              color: year === y ? T.navy : "rgba(255,255,255,.8)",
            }}
          >
            {y}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff" }}>
        {rows.length === 0 && <EmptyState>Nothing recorded in {year} yet.</EmptyState>}
        {rows.map((r, i) =>
          r.kind === "gap" ? (
            <button
              key={r.date}
              onClick={() => onFillGap(r.date)}
              className="row-press"
              style={{
                width: "100%",
                textAlign: "left",
                padding: "13px 18px",
                borderBottom: `1px solid ${T.line}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#FCFAF5",
              }}
            >
              <span className="tnum" style={{ fontSize: 14, color: T.brass, fontWeight: 500 }}>
                {fmtDate(r.date)}
              </span>
              <span style={{ fontSize: 12.5, color: T.brass, display: "flex", alignItems: "center", gap: 5 }}>
                Not recorded <Plus size={14} />
              </span>
            </button>
          ) : (
            <MeetingRow key={r.m.id ?? i} m={r.m} nameOf={nameOf} onEdit={() => onEdit(r.m)} />
          ),
        )}
      </div>
    </>
  );
}

function MeetingRow({
  m,
  nameOf,
  onEdit,
}: {
  m: Meeting;
  nameOf: (a: Assignment) => string;
  onEdit: () => void;
}) {
  const speakers = m.assignments.filter((a) => a.role === "speaker");
  const prayers = m.assignments.filter((a) => a.role !== "speaker");
  const t = typeOf(m.meeting_type);

  return (
    <div style={{ padding: "13px 8px 13px 18px", borderBottom: `1px solid ${T.line}`, display: "flex", gap: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span className="tnum" style={{ fontSize: 14, fontWeight: 600 }}>
            {fmtDate(m.meeting_date)}
          </span>
          {m.meeting_type !== "regular" && (
            <span
              style={{
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: T.accent,
                background: T.accentSoft,
                padding: "3px 6px",
                borderRadius: 4,
                whiteSpace: "nowrap",
              }}
            >
              {t.label}
            </span>
          )}
        </div>

        {speakers.length > 0 && (
          <div className="serif" style={{ fontSize: 16, marginTop: 5, lineHeight: 1.45 }}>
            {speakers.map(nameOf).join(" · ")}
          </div>
        )}
        {speakers.length === 0 && t.speakers && (
          <div style={{ fontSize: 13, color: T.faint, marginTop: 4 }}>No speakers recorded</div>
        )}
        {/* Slash-separated, not comma: names are already "Last, First", so
            commas between them read as one long run of names. */}
        {prayers.length > 0 && (
          <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>Prayers: {prayers.map(nameOf).join(" / ")}</div>
        )}
        <div style={{ fontSize: 11, color: T.faint, marginTop: 6 }}>Recorded {elapsed(m.created_at.slice(0, 10))}</div>
      </div>

      <button
        onClick={onEdit}
        aria-label={`Edit ${fmtDate(m.meeting_date)}`}
        style={{ color: T.faint, padding: "6px 10px", alignSelf: "flex-start", display: "flex" }}
      >
        <Pencil size={16} />
      </button>
    </div>
  );
}
